"use client"

import React, { useEffect, useMemo, useState } from 'react'
import DashboardContentWrapper from '@/shared/components/dashboard-content-wrapper'
import { authFetchJson } from '@/shared/lib/auth-fetch'
import Heading from '@/shared/components/ui/typography/heading'
import PartnerForm from '@/features/partners/components/partner-form'
import PartnerFormProvider from '@/features/partners/components/forms/providers/partner-form-provider'
import { ChevronLeft } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { buildRegionUrl } from '@/shared/lib/utils'
import { useRegion } from '@/shared/hooks/use-region'
import { SEARCH_PARAMS_TAB_KEY } from '@/features/admin/constants/admin-tabs'
import { Partner, PartnerRegionAssociation } from '@/features/partners/types/partner.type'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import ScrollToTopFab from '@/shared/components/scroll-to-top-fab'

function extractAssociations(raw: unknown): PartnerRegionAssociation[] {
  if (Array.isArray(raw)) {
    return raw as PartnerRegionAssociation[]
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    if (obj.data !== undefined) {
      return extractAssociations(obj.data)
    }
  }
  return []
}

function extractOrganizations(raw: unknown): Partner[] {
  if (Array.isArray(raw)) {
    return raw as Partner[]
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    if (obj.data !== undefined) {
      return extractOrganizations(obj.data)
    }
  }
  return []
}

export default function CreatePartnerPage() {
  const router = useRouter()
  const { regionId, regionSlug, region } = useRegionId()
  const { regionSlug: regionSlugFromHook } = useRegion()
  const { session, isLoading: sessionLoading } = useSession()
  const [error, setError] = useState<string | null>(null)
  const [organizations, setOrganizations] = useState<Partner[]>([])
  const [associations, setAssociations] = useState<PartnerRegionAssociation[]>([])
  const [loadingOrganizations, setLoadingOrganizations] = useState(false)
  
  // Only ADMIN and BASE_LEADER can create partners
  const canManagePartners = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
  
  // Redirect if user doesn't have permission
  useEffect(() => {
    if (!sessionLoading && !canManagePartners) {
      const href = buildRegionUrl(regionSlugFromHook || regionSlug, '/partners')
      router.push(href)
    }
  }, [sessionLoading, canManagePartners, router, regionSlugFromHook, regionSlug])
  
  // Fetch supporting data - only if user has permission
  useEffect(() => {
    if (!canManagePartners || !regionId) return
    
    const fetchSupportingData = async () => {
      setLoadingOrganizations(true)
      try {
        const [orgResponse, associationResponse] = await Promise.all([
          authFetchJson<any>('/organizations'),
          authFetchJson<any>(`/organization-regions?regionId=${regionId}`),
        ])

        if (orgResponse.success) {
          setOrganizations(extractOrganizations(orgResponse.data))
        } else {
          setOrganizations([])
        }

        if (associationResponse.success) {
          setAssociations(extractAssociations(associationResponse.data))
        } else {
          setAssociations([])
        }
      } finally {
        setLoadingOrganizations(false)
      }
    }

    fetchSupportingData()
  }, [regionId, canManagePartners])

  const availableExistingOrganizations = useMemo(() => {
    if (!organizations.length) return []
    const associatedIds = new Set(associations.map((assoc) => assoc.organizationId))
    return organizations.filter((org) => !associatedIds.has(org.id))
  }, [organizations, associations])
  
  // Show loading state while checking permissions
  if (sessionLoading || !canManagePartners) {
    return (
      <DashboardContentWrapper className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardContentWrapper>
    )
  }

  const handleBack = () => {
    const href = buildRegionUrl(regionSlugFromHook || regionSlug, '/admin', { [SEARCH_PARAMS_TAB_KEY]: 'partners' })
    router.push(href)
  }

  const handleSubmit = async (data: any) => {
    if (!regionId) {
      setError('Region not available. Please select a region and try again.')
      return
    }

    try {
      setError(null)

      const {
        mode,
        existingOrganizationId,
        regionalContactPerson,
        regionalContactPhone,
        regionalContactEmail,
        regionalIsActive,
        regionAssociationId: _ignoredAssociationId,
        ...organizationPayload
      } = data

      if (mode === 'existing') {
        const organizationId = Number(existingOrganizationId)
        if (!organizationId) {
          setError('Please select an organization to associate.')
          return
        }

        const associationResponse = await authFetchJson<any>('/organization-regions', {
          method: 'POST',
          body: JSON.stringify({
            organizationId,
            regionId,
            isActive: regionalIsActive,
            contactPerson: regionalContactPerson,
            contactPhone: regionalContactPhone,
            contactEmail: regionalContactEmail,
          }),
        })

        if (!associationResponse.success) {
          throw new Error(associationResponse.message ?? 'Failed to associate organization with region')
        }
      } else {
        const response = await authFetchJson<any>('/organizations', {
          method: 'POST',
          body: JSON.stringify(organizationPayload),
        })

        if (!response.success) {
          throw new Error(response.message ?? 'Failed to create organization')
        }

        const createdOrganization = response.data

        const associationResponse = await authFetchJson<any>('/organization-regions', {
          method: 'POST',
          body: JSON.stringify({
            organizationId: createdOrganization.id,
            regionId,
            isActive: regionalIsActive,
            contactPerson: regionalContactPerson,
            contactPhone: regionalContactPhone,
            contactEmail: regionalContactEmail,
          }),
        })

        if (!associationResponse.success) {
          throw new Error(associationResponse.message ?? 'Failed to associate organization with region')
        }
      }

      const href = buildRegionUrl(regionSlugFromHook || regionSlug, '/admin', { [SEARCH_PARAMS_TAB_KEY]: 'partners' })
      router.push(href)
    } catch (err) {
      console.error('Error creating partner:', err)
      setError(err instanceof Error ? err.message : 'Unexpected error creating partner')
    }
  }

  return (
    <DashboardContentWrapper className="flex flex-col gap-7 pb-24 md:pb-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-3 md:pb-2">
        <div className="flex items-center gap-3">
          <button onClick={handleBack}>
            <ChevronLeft className="w-7 h-7" />
          </button>
          <div>
            <Heading>{region?.name ? `Add Partner for ${region.name}` : 'Add New Partner'}</Heading>
            <p className="text-muted-foreground text-sm mt-1">
              Create a new partner organization or associate an existing partner with this region
            </p>
          </div>
        </div>
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>

      <PartnerFormProvider onSubmit={handleSubmit}>
        <PartnerForm existingOrganizations={availableExistingOrganizations} existingOrganizationsLoading={loadingOrganizations} />
      </PartnerFormProvider>
      <ScrollToTopFab />
    </DashboardContentWrapper>
  )
}
